//Imports
import React, { useEffect, useState, useCallback } from "react";
import moment from 'moment';
import _ from 'lodash';

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

import { Avatar } from 'Components';

import TableView from 'Components/TableView';

//Controls
import {
    Button,
    Form,
    Modal,
    DatePicker,
    SearchInput
} from "@gpstrackit/gtc-react-controls";
import { Icon } from '@mui/material';

const ROWS_PER_PAGE = 50;

//Slice
import {
    workOrdersSearchAvailableDrivers,
    reset,
} from "./redux/slice";

const INIT_DATA = {
    scheduledAt: null,
    driver: null
};

//Styles
import './index.scss';

const DriverSelector = () => {

    const dispatch = useDispatch();
    const { messages } = useIntl();

    //States
    const [formData, setFormData] = useState(INIT_DATA);
    const [openSearch, setOpenSearch] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [page, setPage] = useState(0);

    //Form refs
    const driverSelectorWorkOrderFormRef = Form.useForm();

    //Redux
    const {
        drivers,
        loading,
        open,
        scheduledAt,
        estimatedDurationMins,
        tagIds,
        driver,
        total
    } = useSelector((state) => state.workOrdersDriverSelectorRedux);
    const { workOrder } = useSelector((state) => state.workOrdersSettingsWorkOrderRedux);

    //Handlers and utils
    const handleClose = useCallback(() => {
        setOpenSearch("")
        setFormData(INIT_DATA);
        dispatch(reset());
        setFilteredData([])
        setSelectedDriver(null)
    });

    const searchAvailableDrivers = ({ openSearch, dateRange, tagIds, reset, offset }) => {
        dispatch(workOrdersSearchAvailableDrivers({
            openSearch: openSearch,
            dateRange: dateRange,
            tagIds: tagIds,
            excludedWorkOrderIds: workOrder?.id ? [workOrder?.id] : [],
            reset: reset,
            limit: ROWS_PER_PAGE,
            offset: offset,
        }))
    }

    const changeDate = (value) => {
        setFormData({ ...formData, driverScheduledAt: value })
        dispatch(reset({ scheduledAt: value }));
        const startMoment = moment(value).utc();
        const endMoment = moment(value).add(estimatedDurationMins, 'minutes').utc();
        setSelectedDriver(null)
        searchAvailableDrivers({
            openSearch: openSearch,
            dateRange: { start: startMoment.toISOString(), end: endMoment.toISOString() },
            tagIds: tagIds,
            reset: true,
            offset: 0,
        })
        setPage(1);
        setSelectedDriver(null)
        setOpenSearch("");
        setFilteredData([]);
    }

    const applyChanges = () => {
        let driverScheduledAt = formData?.driverScheduledAt || scheduledAt;
        const startMoment = moment(driverScheduledAt).utc();
        dispatch(reset({
            open: false,
            drivers: [],
            estimatedDuration: 0,
            selectedDriver: {
                ...selectedDriver,
                scheduledAt: startMoment?.toISOString()
            }
        }));
    }

    useEffect(() => {
        if (open && scheduledAt) {
            setFormData({ ...formData, driverScheduledAt: scheduledAt })
        }
    }, [open])

    const onSearch = (value) => {
        setOpenSearch(value);

        if (window.typingTime) {
            clearTimeout(window.typingTime);
        }
        window.typingTime = setTimeout(() => {
            setSelectedDriver(null)
            let driverScheduledAt = formData?.driverScheduledAt || scheduledAt;
            const startMoment = moment(driverScheduledAt).utc();
            const endMoment = moment(driverScheduledAt).add(estimatedDurationMins, 'minutes').utc();
            searchAvailableDrivers({
                openSearch: value,
                dateRange: { start: startMoment.toISOString(), end: endMoment.toISOString() },
                tagIds: tagIds,
                reset: true,
                offset: 0,
            })
            setPage(1);
            window.typingTime = null;
        }, 1000);
    };

    useEffect(() => {
        let currentDrivers = [...drivers]
        // Sort filteredData alphabetically by name using lodash (case insensitive)
        setFilteredData(_.orderBy(currentDrivers, [driver => driver.name.toLowerCase()], ['asc']))
    }, [drivers])

    useEffect(() => {
        if (filteredData.length && driver) {
            let currentDriver = filteredData.find((item) => item?.id == driver?.id)
            if (currentDriver) {
                setSelectedDriver(currentDriver)
            } else {
                setSelectedDriver(driver)
            }
            dispatch(reset({ driver: null }))
        }
    }, [filteredData, driver])

    

    const onChangePage = (paramPage) => {
        if (loading) return //prevents multiple requests
        const newOffset = (paramPage - 1) * ROWS_PER_PAGE;

        let driverScheduledAt = formData?.driverScheduledAt || scheduledAt;

        const startMoment = moment(driverScheduledAt).utc();
        const endMoment = moment(driverScheduledAt).add(estimatedDurationMins, 'minutes').utc();

        setPage(paramPage);

        searchAvailableDrivers({
            openSearch: openSearch,
            dateRange: { start: startMoment, end: endMoment },
            tagIds: tagIds,
            reset: false,
            offset: newOffset
        })
    }

    return (
        <Modal
            title={messages['workOrder_driverSelector_title']}
            open={open}
            handleClose={handleClose}
            position="right"
            style={{ zIndex: "auto", overflow: 'hidden' }}
            size="small"
            footer={
                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Button
                        isDisabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={handleClose}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading || !selectedDriver?.id}
                        onClick={() => {
                            driverSelectorWorkOrderFormRef?.submit()
                        }}
                        variant="primary"
                    >
                        {messages["select"]}
                    </Button>
                </div>
            }
        >
            <div className="driver-selector">
                <Form
                    ref={driverSelectorWorkOrderFormRef.ref}
                    loading={loading}
                    onValidSubmit={() => applyChanges()}
                >
                    {
                        formData?.driverScheduledAt ? (
                            <div style={{ marginBottom: 10 }}>
                                <div style={{ marginBottom: 5 }}><span className="required-gtc-rc">*</span><label className="label-gtc-rc">{messages['workOrder_driverSelector_scheduledAt']}</label></div>
                                <DatePicker
                                    id="driverScheduledAt"
                                    minDate={new Date()}
                                    placeholder={messages['workOrder_driverSelector_scheduledAt']}
                                    value={formData?.driverScheduledAt}
                                    showTimePicker
                                    onChange={(value) => changeDate(value)}
                                    isDisabled={loading}
                                />
                            </div>
                        ) : <></>
                    }
                </Form>
                <div className="driver-selector-search-container">
                    <SearchInput
                        placeholder={messages['search']}
                        searchText={openSearch}
                        isDisabled={loading}
                        onSearchText={(text) => onSearch(text)}
                    />
                </div>
                <div style={{ marginBottom: 5 }}>
                    {filteredData.length > 0 && (<span>{messages['workOrder_driverSelector_driversAvailable']}</span>)}
                </div>
                <div className="driver-selector-list-container">
                    <TableView
                        data={filteredData || []}
                        loading={loading}
                        page={page}
                        rowsPerPage={ROWS_PER_PAGE}
                        name="drivers-list"
                        topSpaceSize={280}
                        infiniteScroll={true}
                        viewType="list"
                        messages={messages}
                        itemSelector={false}
                        serverSide
                        onChangePage={onChangePage}
                        total={total}
                        moreRowsAvailable={filteredData?.length || 0 < total}
                        renderRow={(item) => {
                            return (
                                <div style={{ backgroundColor: selectedDriver?.id == item?.id && '#e4e3e3' }} key={item?.id} className="driver-item" onClick={() => setSelectedDriver(item)}>
                                    <div className="driver-avatar">
                                        <Avatar fileKey={item?.fileKey}  name={item?.name} />
                                    </div>
                                    <div>
                                        <div className="driver-name">{item?.name}</div>
                                        <div style={{ display: 'flex' }}>
                                            <strong style={{ marginRight: 5 }}>{messages['workOrder_driverSelector_unit']}</strong>
                                            {
                                                !item?.unitName && (
                                                    <Icon
                                                        style={{
                                                            margin: '0 5px 0 0',
                                                            fontSize: 17,
                                                            color: "#FFDC00"
                                                        }}
                                                    >
                                                        warning
                                                    </Icon>
                                                )
                                            }
                                            {` ${item?.unitName || messages['workOrder_driverSelector_unassigned']}`}
                                        </div>
                                    </div>
                                </div>
                            )
                        }}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default DriverSelector;